import {useEffect, useCallback, useMemo, useState} from "react";
import useAPIService from "./useAPIService";

// Defensively coerce/sanitize each row coming back from the API. The
// backend has at times stored YouTube uploads with `videoLength: ""`
// (saved before getVideoInfo resolved), and rows missing an `id` would
// crash the admin grid when used as a React key or as a delete target.
// We coerce the length to a non-negative integer and drop any row
// without a usable id/url so downstream code can assume the shape.
const normalizeVideoRow = (row) => {
  if (!row || typeof row !== "object") return null;
  if (!row.id || !row.url) return null;
  const lengthNumber = Number(row.videoLength);
  return {
    ...row,
    videoLength:
      Number.isFinite(lengthNumber) && lengthNumber > 0 ? lengthNumber : 0,
  };
};

export const useVideoUpload = () => {
  const {apiFetch} = useAPIService();
  const [videos, setVideos] = useState([]);

  const loadVideos = useCallback(async () => {
    const response = await apiFetch("/get-videos");
    if (response.result === "success") {
      const cleaned = (response.data || [])
        .map(normalizeVideoRow)
        .filter(Boolean);
      setVideos(cleaned);
      return cleaned;
    }
    setVideos([]);
    return [];
  }, [apiFetch]);

  useEffect(() => {
    loadVideos().catch(console.error);
  }, [loadVideos]);

  const createVideo = async ({title, category, url, videoLength}) => {
    const response = await apiFetch("/add-video", {
      method: "POST",
      body: JSON.stringify({title, category, url, videoLength}),
    });
    if (response.result === "success") {
      return response.data;
    }
    throw new Error("Failed to create video!");
  };

  const createVideoUpload = async ({fileName, contentType}) => {
    const response = await apiFetch("/create-video-upload", {
      method: "POST",
      body: JSON.stringify({fileName, contentType}),
    });
    if (response.result === "success") {
      return response.data;
    }
    throw new Error("Failed to create video upload URL!");
  };

  const updateVideo = async ({id, category}) => {
    const response = await apiFetch("/update-video", {
      method: "PATCH",
      body: JSON.stringify({id, category}),
    });
    if (response.result === "success") {
      return response.data;
    }
    throw new Error("Failed to update video!");
  };

  const deleteVideo = async (_id) => {
    const response = await apiFetch("/delete-video", {
      method: "DELETE",
      body: JSON.stringify({id: _id}),
    });
    if (response.result === "success") {
      return response.data;
    }
    throw new Error("Failed to delete video!");
  };

  const categories = useMemo(() => {
    // `new Set(...).values().toArray()` relies on the Stage-3 Iterator
    // Helpers proposal (`Iterator.prototype.toArray`). It only ships in
    // V8 12.2+/Safari 17.4+, so older browsers throw TypeError on this
    // line and the entire admin upload page renders blank (white screen
    // reported via Asana). `Array.from` is the universally supported
    // equivalent. We also drop falsy/empty categories so the dropdown
    // doesn't show a blank "" option.
    const unique = new Set(
      (videos || [])
        .map((el) => (el?.category || "").trim())
        .filter((c) => c.length > 0),
    );
    return Array.from(unique).sort((a, b) =>
      a.localeCompare(b, undefined, {sensitivity: "base"}),
    );
  }, [videos]);

  return {
    videos,
    categories,
    loadVideos,
    createVideo,
    createVideoUpload,
    updateVideo,
    deleteVideo,
  };
};
